
import RcDomainObject from "../../common/RcDomainObject";
import CheckListMap from "../checklist/CheckListMap.js";
import Schedule from "../schedule/Schedule.js";
import Category from "../category/Category.js";
import Company from "../company/Company.js";
import ImageMap from "../image/ImageMap.js";
import Image from "../image/Image.js";
import StringUtils from "@/utils/StringUtils.js";
import ContentUtils from "@/utils/ContentUtils.js";
import DateUtils from "@/utils/DateUtils.js";
import RcDate from "@/domain/session/time/RcDate.js";
import RcTime from "@/domain/session/time/RcTime.js";
import User from "../user/User.js";
import VehicleUtils from "./VehicleUtils.js";
import LabelMap from "../label/LabelMap.js";

import VehicleDataMap from "./data/VehicleDataMap.js";
import VehicleData from "./data/VehicleData.js";

export default class Vehicle extends RcDomainObject {

  static MODEL_NAME = "Vehicle";

  /** How recent a tracker's last position must be for the vehicle to count as on the road. */
  static RECENT_REPORT_MS = 30 * 60 * 1000;

  static FIELDS = {
    NAME: 'name',
    _CATEGORY: '_category',
    CATEGORY: Category.MODEL_NAME,
    COMPANY: Company.MODEL_NAME,
    JURISDICTION: 'jurisdiction',
    SCHEDULE: 'VehicleSchedule',
    PLATE: 'plate',
    DEFECT_TYPE: 'defectType',
    BRAKE_TYPE: 'brakingType',
    ODOMETER: 'odometer',
    ODOMETER_TYPE: "odometerType",
    HUBOMETER_USED: 'hubometerUsed',
    SUMMARY: 'summary',
    VIN: 'vin',
    MAKE: 'make',
    MODEL: 'model',
    ANNUAL_DATE: 'cvorDate',
    ANNUAL_NUMBER: 'cvorNumber',
    YEAR: 'year',
    COLOUR: 'colour',
    WEIGHT: 'weight',
    IMAGE_DATA: "imageData",
    Checklists: CheckListMap.MODEL_NAME,
    CHECKLIST_MAP: CheckListMap.MODEL_NAME,
    Images: ImageMap.MODEL_NAME,
    IMAGE_URL: "image",
    USAGE_TYPE: "usageType",
    UNEXPECTED_MOTION: "unexpectedMotion",
    OUTSIDE_160: "outside160",

    PLATE_EXPIRY_DATE: 'plateExpiryDate',
    END_DATE: "end",
    START_DATE: "start",
    NOTES: "notes",
    LABELS: "LabelMap",

    LAT: 'lat',
    LON: 'lon',
    SPEED: 'speed',
  }

  static Values = {
    Odometer: {
      Type: {
        KM: 'KM',
        MI: 'MI',
      }
    },

    UsageType: {
      COMMERCIAL: "commercial",
      NONCOMMERCIAL: "noncommercial",
    }
  }

  constructor(domain, data) {
    super(domain, data, Vehicle.MODEL_NAME);
  }

  fields() {
    return Vehicle.FIELDS;
  }

  find() {
    return this.domain.vehicles().findById(this.id());
  }

  weight() {
    return this.getString(Vehicle.FIELDS.WEIGHT);
  }

  withWeight(weight) {
    this.put(Vehicle.FIELDS.WEIGHT, weight);
    return this;
  }

  hasWeightSet() {
    return this.hasWeight();
  }

  hasWeight() {
    var weight = this.getInt(Vehicle.FIELDS.WEIGHT);
    if (weight > 0) {
      return true;
    }
    return false;
  }

  imageUrl() {
    return this.getString(Vehicle.FIELDS.IMAGE_URL);
  }

  hasImageUrl() {
    return !StringUtils.isEmpty(this.imageUrl());
  }

  images() {
    var value = this.get(Vehicle.FIELDS.Images);
    var images = new ImageMap(this.domain, value);
    if (this.hasImageUrl() && images.isEmpty()) {
      var image = new Image(this.domain);
      image.withUrl(this.imageUrl());
      images.add(image);
    }
    return images;
  }

  withImages(imageMap) {
    this.put(Vehicle.FIELDS.Images, imageMap);
    return this;
  }

  withImageData(data) {
    this.put(Vehicle.FIELDS.IMAGE_DATA, data);
    return this;
  }

  imageData() {
    return this.getString(Vehicle.FIELDS.IMAGE_DATA);
  }

  notes() {
    return this.getString(Vehicle.FIELDS.NOTES);
  }

  withNotes(notes) {
    this.put(Vehicle.FIELDS.NOTES, notes);
    return this;
  }

  plate() {
    return this.getString(Vehicle.FIELDS.PLATE);
  }

  withPlate(plate) {
    this.put(Vehicle.FIELDS.PLATE, plate);
    return this;
  }

  isPmviValid() {
    return !StringUtils.isEmpty(this.annualDate());
  }

  withStartDate(date) {
    this.put(Vehicle.FIELDS.START_DATE, date);
    return this;
  }

  startDate() {
    return this.getString(Vehicle.FIELDS.START_DATE);
  }

  withEndDate(date) {
    this.put(Vehicle.FIELDS.END_DATE, date);
    return this;
  }

  endDate() {
    return this.getString(Vehicle.FIELDS.END_DATE);
  }

  isPlateExpiryDateValid() {
    return !StringUtils.isEmpty(this.plateExpiryDate());
  }

  plateExpiryDate() {
    return this.getString(Vehicle.FIELDS.PLATE_EXPIRY_DATE);
  }

  withPlateExpiryDate(date) {
    this.put(Vehicle.FIELDS.PLATE_EXPIRY_DATE, date);
    return this;
  }

  plateExpiryDateV2() {
    var dateValue = this.plateExpiryDate();
    var timeZone = this.timeZone();
    var rcDate = new RcDate(new Date(parseInt(dateValue)), timeZone);
    return rcDate;
  }

  /**
   * A plate is good to the end of the month printed on it, so a plate dated the 15th is
   * still valid on the 30th. The java Vehicle, the server's plate filters and the expiry
   * reminder emails all count to the same moment; every plate method below uses it.
   */
  plateExpiryEndDate() {
    return this.plateExpiryDateV2().month().last().end();
  }

  hasPlateExpired() {
    if (!this.isPlateExpiryDateValid()) {
      return false;
    }
    var now = this.domain.session().time().now();
    return this.plateExpiryEndDate().time() < now.time();
  }

  willPlateExpireInWeeks(weeks) {
    if (!this.isPlateExpiryDateValid()) {
      return false;
    }
    var warnFrom = this.plateExpiryEndDate().time() - DateUtils.days(weeks * 7);
    var now = this.domain.session().time().now();
    return warnFrom <= now.time();
  }

  /** Whole days from today to the plate's last day; 0 on that day and once it has passed. */
  daysLeftBeforePlateExpires() {
    if (!this.isPlateExpiryDateValid()) {
      return -1;
    }
    var today = this.domain.session().time().now().day().end();
    var ms = this.plateExpiryEndDate().time() - today.time();
    var daysLeft = Math.round(DateUtils.daysFromMs(ms));
    if (daysLeft < 0) {
      daysLeft = 0;
    }
    return daysLeft;
  }

  plateExpiryDateDisplay() {
    if (!this.isPlateExpiryDateValid()) {
      return "";
    }

    var rcDate = this.plateExpiryDateV2();
    return rcDate.displayAs_YYYY_MM_DD();
  }

  withAnnualDate(date) {
    this.put(Vehicle.FIELDS.ANNUAL_DATE, date);
    return this;
  }

  safetyDate() {
    return this.annualDateV2();
  }

  annualDateV2() {
    var dateValue = this.annualDate();
    var timeZone = this.timeZone();
    var rcDate = new RcDate(new Date(parseInt(dateValue)), timeZone);
    return rcDate;
  }

  annualDate() {
    return this.getString(Vehicle.FIELDS.ANNUAL_DATE);
  }

  isLastSafetyDateValid() {
    return !StringUtils.isEmpty(this.annualDate());
  }

  annualDateDisplay() {
    if (!this.isPmviValid()) {
      return "";
    }

    var rcDate = this.annualDateV2();
    return rcDate.displayAs_YYYY_MM_DD();
  }

  plateExpiryDateDaysLeft() {
    if (!this.isPlateExpiryDateValid()) {
      return "";
    }
    return this.daysLeftBeforePlateExpires();
  }


  hasLastSafetyDateExpired() {
    return this.hasAnnualDateExpired();
  }

  hasAnnualDateValue() {
    var dateValue = this.annualDate();
    if (StringUtils.isEmpty(dateValue)) {
      return false;
    }
    return true;
  }

  hasAnnualDateExpired() {
    var dateValue = this.annualDate();
    if (StringUtils.isEmpty(dateValue)) {
      return false;
    }
    var daysRemaining = this.annualDateDaysLeft();
    var expired = daysRemaining <= 0;
    return expired;
  }

  willLastSafetyDateExpireInWeeks(weeks) {
    return this.willAnnualDateExpireInWeeks(weeks);
  }

  willAnnualDateExpireInWeeks(weeks) {
    var dateValue = this.annualDate();
    if (StringUtils.isEmpty(dateValue)) {
      return false;
    }
    var rcDate = this.annualDateV2();
    var rcMonth = rcDate.month();
    var lastDay = rcMonth.lastDay();
    var lastHour = lastDay.lastHour();
    var day = lastHour.day();
    var weeksAgo2 = day.weeksAgo(weeks);
    var nextYear = weeksAgo2.year().next();
    if (this.isBus()) {
      nextYear = rcDate.month().plus(6).lastDay().weeksAgo(weeks).year();
    }
    var now = this.domain.session().time().now();
    return nextYear.time() <= now.time();
  }

  annualDateDaysLeft() {
    var dateValue = this.annualDate();
    if (StringUtils.isEmpty(dateValue)) {
      return "";
    }
    var rcDate = this.annualDateV2();
    var rcMonth = rcDate.month();
    var lastDay = rcMonth.lastDay().lastHour().day();
    var nextYear = lastDay.year().next().day().year();
    if (this.isBus()) {
      nextYear = lastDay.month().firstDay().month().plus(6).lastDay().year()
    }
    var now = this.domain.session().time().now();
    var nowDay = now.day();
    var ms = nextYear.time() - nowDay.time();
    var daysRemaining = DateUtils.daysFromMs(ms);
    var daysLeft = Math.floor(daysRemaining);
    if (daysLeft < 0) {
      daysLeft = 0;
    }
    return daysLeft;
  }

  safetyNumber() {
    return this.annualNumber();
  }

  annualNumber() {
    return this.getString(Vehicle.FIELDS.ANNUAL_NUMBER);
  }

  withAnnualNumber(number) {
    this.put(Vehicle.FIELDS.ANNUAL_NUMBER, number);
    return this;
  }

  hasOdometerSet() {
    var value = this.odometer();
    return StringUtils.isNotEmpty(value);
  }

  // Asks the recorded reading, not hours(): that stands in "0" for a missing one,
  // so testing it said every asset had a reading and the Hours column never
  // showed "Not set".
  hasHoursSet() {
    var value = this.vehicleData().find().hours().value();
    return StringUtils.isNotEmpty(value);
  }

  hours() {
    let value = this.vehicleData().find().hours().value();
    if (StringUtils.isEmpty(value)) {
      value = "0";
    }
    return value;
  }

  // A correction to the hour meter. The server ignores the readings a client
  // sends back, so this goes as a plain "hours" value that the save turns into
  // the asset's hours reading; it is never stored as a property of its own.
  withHours(hours) {
    // String(), not StringUtils.toString: that turns a reading of 0 into "".
    this.put(VehicleData.Values.Keys.Hours, String(hours ?? ""));
    return this;
  }

  // The latest values recorded against this asset - odometer, meter readings.
  // Travels with the asset, so a reading can be compared against the one before
  // it without going back to the inspection history.
  vehicleData() {
    var data = this.get(VehicleDataMap.MODEL_NAME);
    return new VehicleDataMap(this.domain, data);
  }

  withVehicleData(map) {
    this.put(VehicleDataMap.MODEL_NAME, map);
    return this;
  }

  // The latest value the server has recorded against this asset for what was
  // measured - a checklist item id, or the odometer - or "" when there is none.
  reading(key) {
    return this.vehicleData().find().forKey(key).value();
  }

  odometer() {
    let value = this.vehicleData().find().odometer().value();
    if (StringUtils.isEmpty(value)) {
      // Assets last written before the recorded values existed still carry the
      // odometer as a plain property.
      value = this.getString(Vehicle.FIELDS.ODOMETER);
    }
    if (StringUtils.isEmpty(value)) {
      value = "0";
    }
    return value;
  }

  withOdometer(odometer) {
    this.put(Vehicle.FIELDS.ODOMETER, odometer);
    return this;
  }

  odometerType() {
    var value = this.getString(Vehicle.FIELDS.ODOMETER_TYPE);
    return value;
  }

  withOdometerType(type) {
    this.put(Vehicle.FIELDS.ODOMETER_TYPE, type);
    return this;
  }

  defectType() {
    return this.getString(Vehicle.FIELDS.DEFECT_TYPE);
  }

  displayName() {
    if (StringUtils.isEmpty(this.plate())) {
      return this.name();
    }
    return StringUtils.format("{0} ({1})", [this.name(), this.plate()]);
  }

  displayNameDetails() {
    if (StringUtils.isEmpty(this.plate())) {
      return this.name();
    }
    return StringUtils.format("{0} ({1} {2})", [this.name(), this.plate(), this.makeModelDisplay()]);
  }

  defectTypeDisplay() {
    var defectType = this.defectType();
    return VehicleUtils.defectDisplayValue(defectType);
  }

  isDefectTypeGood() {
    var defectType = this.defectType();
    // Matched without regard to case, the way the severity checks below are: the value
    // is stored as "None" but reaches us as "none" often enough that an exact compare
    // left those readings counting as neither good nor defective.
    if (StringUtils.isEmpty(defectType) || StringUtils.eic(defectType, "none")) {
      return true;
    }
    return false;
  }

  isDefectTypeMinor() {
    var defectType = this.defectType();
    if (StringUtils.eic(defectType, "minor")) {
      return true;
    }
    return false;
  }

  isDefectTypeMajor() {
    var defectType = this.defectType();
    if (StringUtils.eic(defectType, "major")) {
      return true;
    }
    return false;
  }

  brakeType() {
    return this.getString(Vehicle.FIELDS.BRAKE_TYPE);
  }

  withBrakeType(type) {
    this.put(Vehicle.FIELDS.BRAKE_TYPE, type);
    return this;
  }

  brakeTypeDisplay() {
    var type = this.brakeType();
    if (type === "abs") {
      return "Air Brakes";
    }
    if (type === "ebs") {
      return "Electric Brakes";
    }
    if (type === "hbs") {
      return "Hydraulic Brakes";
    }
    return "None";
  }

  vin() {
    return this.getString(Vehicle.FIELDS.VIN);
  }

  withVin(vin) {
    this.put(Vehicle.FIELDS.VIN, vin);
    return this;
  }

  categoryDisplayName() {
    return this.findCategoryById(this.category().id()).name;
  }

  findCategoryById(id) {
    let cats = ContentUtils.CATEGORIES();
    for (var cat in cats) {
      let item = cats[cat];
      if (item['@rid'] === id) {
        return item;
      }
    }
    return { name: '' };
  }

  category() {
    var value = this.get(Vehicle.FIELDS.CATEGORY);
    return new Category(this.domain, value);
  }

  withLabels(labelMap) {
    this.put(Vehicle.FIELDS.LABELS, labelMap);
    return this;
  }

  labels() {
    const value = this.get(Vehicle.FIELDS.LABELS);
    return new LabelMap(this.domain, value);
  }

  withCompany(company) {
    this.put(Vehicle.FIELDS.COMPANY, company);
    return this;
  }

  company() {
    var value = this.get(Vehicle.FIELDS.COMPANY);
    return new Company(this.domain, value);
  }


  withCategory(category) {
    this.put(Vehicle.FIELDS.CATEGORY, category);
    return this;
  }

  with_Category(category) {
    this.put(Vehicle.FIELDS._CATEGORY, category);
    return this;
  }

  _category() {
    var value = this.getString(Vehicle.FIELDS._CATEGORY);
    return value;
  }

  year() {
    var value = this.getString(Vehicle.FIELDS.YEAR);
    return value;
  }

  withYear(year) {
    this.put(Vehicle.FIELDS.YEAR, year);
    return this;
  }

  checklists() {
    var value = this.get(Vehicle.FIELDS.CHECKLIST_MAP);
    return new CheckListMap(this.domain, value);
  }

  schedule() {
    var value = this.get(Vehicle.FIELDS.SCHEDULE);
    return new Schedule(this.domain, value);
  }

  withSchedule(schedule) {
    this.put(Vehicle.FIELDS.SCHEDULE, schedule);
    return this;
  }

  withChecklist(checklist) {
    this.put(Vehicle.FIELDS.CHECKLIST_MAP, checklist.map());
    return this;
  }

  withChecklists(checklists) {
    this.put(Vehicle.FIELDS.CHECKLIST_MAP, checklists);
    return this;
  }

  summary() {
    return this.getString(Vehicle.FIELDS.SUMMARY);
  }

  withSummary(summary) {
    this.put(Vehicle.FIELDS.SUMMARY, summary);
    return this;
  }

  withColour(colour) {
    this.put(Vehicle.FIELDS.COLOUR, colour);
    return this;
  }

  colour() {
    return this.getString(Vehicle.FIELDS.COLOUR);
  }

  make() {
    return this.getString(Vehicle.FIELDS.MAKE);
  }

  withMake(make) {
    this.put(Vehicle.FIELDS.MAKE, make);
    return this;
  }

  model() {
    return this.getString(Vehicle.FIELDS.MODEL);
  }

  withModel(model) {
    this.put(Vehicle.FIELDS.MODEL, model);
    return this;
  }

  makeModelDisplay() {
    var name = "{0} {1}";
    var make = this.make();
    var model = this.model();
    var value = StringUtils.format(name, [make, model]);
    return StringUtils.trim(value);
  }

  isTruck() {
    return this.isVehicle();
  }

  isTrailer() {
    var category = this.category();
    return category.isTrailer();
  }

  isBus() {
    var category = this.category();
    return category.isBus();
  }

  isLimo() {
    var category = this.category();
    return category.isLimo();
  }

  isSuv() {
    var category = this.category();
    return category.isSuv();
  }

  isVan() {
    var category = this.category();
    return category.isVan();
  }

  isTow() {
    var category = this.category();
    return category.isTow();
  }

  isCar() {
    var category = this.category();
    return category.isCar();
  }

  isVehicle() {
    return !this.isEquipment() && !this.isTrailer();
  }

  isEquipment() {
    var category = this.category();
    return category.isEquipment();
  }

  name() {
    return this.getString(Vehicle.FIELDS.NAME);
  }

  withName(name) {
    this.put(Vehicle.FIELDS.NAME, name);
    return this;
  }

  jurisdiction() {
    return this.getString(Vehicle.FIELDS.JURISDICTION)
  }

  withJurisdiction(jurisdiction) {
    this.put(Vehicle.FIELDS.JURISDICTION, jurisdiction);
    return this;
  }

  copy() {
    var data = this._copy();
    return new Vehicle(this.domain, data);
  }

  modifiedBy() {
    return new User(this.domain, this.get(RcDomainObject.DefaultFields.MODIFIED_BY));
  }

  createdBy() {
    return new User(this.domain, this.get(RcDomainObject.DefaultFields.CREATED_BY));
  }
  withUsageTypeCommercial() {
    return this.withUsageType(Vehicle.Values.UsageType.COMMERCIAL);
  }

  withUsageTypeNonCommercial() {
    return this.withUsageType(Vehicle.Values.UsageType.NONCOMMERCIAL);
  }

  withUsageType(value) {
    this.put(Vehicle.FIELDS.USAGE_TYPE, value);
    return this;
  }

  usageType() {
    let value = this.getString(Vehicle.FIELDS.USAGE_TYPE);
    if (StringUtils.isEmpty(value)) {
      value = Vehicle.Values.UsageType.COMMERCIAL;
    }
    return value;
  }

  isUsageTypeCommercial() {
    if (this.has(Vehicle.FIELDS.USAGE_TYPE)) {
      return this.isUsageType(Vehicle.Values.UsageType.COMMERCIAL);
    }
    return true;
  }

  isUsageType(value) {
    return StringUtils.eic(value, this.usageType());
  }

  withHubometerUsed(value) {
    this.put(Vehicle.FIELDS.HUBOMETER_USED, value);
    return this;
  }

  isHubometerUsed() {
    return this.getBoolean(Vehicle.FIELDS.HUBOMETER_USED);
  }

  hasSchedule() {
    let schedule = this.schedule();
    return schedule.isNotNew();
  }

  hasAtLeastOneChecklist() {
    let checklists = this.checklists();
    return checklists.size() > 0;
  }

  hasGpsDevice() {
    let gpsDevices = this.domain.gpsDevices().findByVehicle(this);
    return gpsDevices.first().isNotNew();
  }

  gpsDevice() {
    let gpsDevices = this.domain.gpsDevices().findByVehicle(this);
    return gpsDevices.first();
  }

  /**
   * A commercial vehicle with a schedule must carry a pre-trip inspection less
   * than 24 hours old. True when this vehicle needs one and none of the
   * inspections loaded is still valid.
   */
  isMissingValidPreTrip() {
    if (!this.hasSchedule() || !this.isUsageTypeCommercial()) {
      return false;
    }
    let preTrip = this.domain.inspections().findLatestPreTripByVehicle(this);
    return preTrip.isNew() || !preTrip.isValid();
  }

  /**
   * Set by the server when the vehicle was seen driving (over 30 km/h, away from HQ) with no
   * pre-trip in the last 24 hours. A pre-trip, or a return to a saved location, clears it.
   */
  hasUnexpectedMotion() {
    return this.getBoolean(Vehicle.FIELDS.UNEXPECTED_MOTION);
  }

  /**
   * The case worth a ticket: the server saw it driving without a pre-trip, and no pre-trip
   * loaded since says otherwise.
   */
  isMovingWithoutValidPreTrip() {
    return this.hasUnexpectedMotion() && this.isMissingValidPreTrip() && this.hasRecentReport();
  }

  /**
   * Whether the vehicle's tracker has reported a position in the last 30 minutes. The trip
   * flags only mean something while the vehicle is out on the road: one whose tracker went
   * quiet long ago cannot be "moving now", whatever flag it was left with.
   */
  hasRecentReport(nowMs = Date.now()) {
    const device = this.gpsDevice();
    if (!device || device.isNew()) {
      return false;
    }
    const reportedAt = parseInt(device.lastPosition().deviceTime());
    return reportedAt > 0 && nowMs - reportedAt <= Vehicle.RECENT_REPORT_MS;
  }

  /**
   * Set by the server when the vehicle's position is more than 160 km from where its pre-trip
   * was done. Cleared when it comes back inside, or by a new pre-trip.
   */
  isOutside160() {
    return this.getBoolean(Vehicle.FIELDS.OUTSIDE_160);
  }

  /**
   * A commercial vehicle with a schedule, on a valid pre-trip, now beyond its 160 km circle:
   * past the short-haul radius the driver needs a full daily log. Without a valid pre-trip the
   * vehicle is flagged as moving without one instead.
   */
  isOutside160WithValidPreTrip() {
    if (!this.hasSchedule() || !this.isUsageTypeCommercial() || !this.isOutside160()) {
      return false;
    }
    return !this.isMissingValidPreTrip() && this.hasRecentReport();
  }

  /**
   * The vehicle's latest inspection for the day a screen is showing: on a past day the latest
   * that day, otherwise the latest in the last 24 hours. inspection() only ever looks back 24
   * hours from now, so on a past date every vehicle looked uninspected.
   *
   * selectedTime is the day's time in milliseconds, as the Map keeps it in the URL; empty means
   * today.
   */
  inspectionOn(selectedTime = null) {
    const rcTime = RcTime.time();
    const time = parseInt(selectedTime);
    let fromMs = rcTime.now().time() - DateUtils.hours24();
    let toMs = Number.MAX_SAFE_INTEGER;
    if (!isNaN(time)) {
      const date = rcTime.at(time);
      if (!rcTime.isToday(date)) {
        fromMs = date.day().firstHour().time();
        toMs = date.day().lastHour().time();
      }
    }
    const inspection = this.domain.inspections().findLatestByVehicleBetween(this, fromMs, toMs);
    return inspection.isNew() ? null : inspection;
  }

  inspection() {
    let inspection = this.domain.inspections().findByVehicle(this);
    if (inspection.isValid()) {
      return inspection;
    }
    return null;
  }

  latitude() {
    return this.get(Vehicle.FIELDS.LAT);
  }

  longitude() {
    return this.get(Vehicle.FIELDS.LON);
  }

  gpsLocation() {
    return StringUtils.format("{0},{1}", this.latitude(), this.longitude())
  }

  speed() {
    const speed = this.getDouble(Vehicle.FIELDS.SPEED);
    return speed;
  }
}